import Bugsnag from '@bugsnag/js';

import { ApiError } from './api-error';

export const REFRESH_INTERVAL = 15000;

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface FetchParams {
  // The URL to send the request to.
  url: RequestInfo;
  //  The HTTP method for the request (e.g., "GET", "POST").
  method?: Method;
  // The request body, which can be any type.
  body?: unknown;
  // The caching mode for the request (e.g., "default", "no-store").
  cache?: RequestCache;
  // Custom headers to include in the request
  headers?: HeadersInit;
}

/**
 * Asynchronous function to fetch data from a specified URL.
 * Always includes authentication from Supabase and supports custom headers.
 */
export default async function clientFetcher<JSON = unknown>({
  url,
  method = 'GET',
  body,
  cache = 'no-store',
  headers = {},
}: FetchParams): Promise<JSON> {
  try {
    let requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Execute request
    const response = await fetch(url, {
      cache: cache,
      method: method,
      body: body ? JSON.stringify(body) : null,
      headers: requestHeaders,
    });

    if (!response.ok) {
      let errorMessage = 'Unknown error occurred';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.title || errorMessage;
      } catch {
        errorMessage = (await response.text()) || errorMessage;
      }

      throw new ApiError(errorMessage, response.status);
    }

    return method !== 'DELETE' ? await response.json() : ({} as JSON);
  } catch (error) {
    Bugsnag.notify(error as Error);
    throw error;
  }
}